import {
  registerUser,
  resendVerificationLetter as resendVerificationLetterAPI,
  setupPassword as setupPasswordAPI,
  verifyEmail as verifyEmailAPI,
} from '../api';
import { Env } from '@/shared/config/environment';
import { LocalStore } from '@/shared/lib/local-storage';

const VERIFY_LETTER_TIMEOUT = 60;

// Одноразовий setupToken живе в sessionStorage цієї вкладки: після оновлення сторінки
// сервер на повторну верифікацію відповідає verified без токена, а користувач ще
// не встановив пароль. Токен належить лише цій вкладці, іншим не потрібен.
const SETUP_TOKEN_KEY = 'register_setup_token';

const readSavedSetupToken = (email) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SETUP_TOKEN_KEY));
    return saved && saved.email === email && saved.token ? saved.token : null;
  } catch (e) {
    return null;
  }
};

const saveSetupToken = (email, token) => {
  try {
    sessionStorage.setItem(SETUP_TOKEN_KEY, JSON.stringify({ email, token }));
  } catch (e) {
    // сховище недоступне — токен лишається лише у store
  }
};

const clearSavedSetupToken = () => {
  try {
    sessionStorage.removeItem(SETUP_TOKEN_KEY);
  } catch (e) {
    // ignore
  }
};

const CHOOSE_ACCOUNT_TYPE_STEP = 'account-type';
const PERSONAL_DATA_STEP = 'personal-data';
const ORGANIZATION_DATA_STEP = 'organization-data';
const PERSONAL_CREDENTIALS_DATA_STEP = 'personal-credential-data';
const ORGANIZATION_CREDENTIALS_DATA_STEP = 'organization-credential-data';
const SENT_CONFIRMATION_LETTER_STEP = 'confirmation-letter';
const SETUP_PASSWORD_STEP = 'setup-password';

const allowedStepSwitches = {
  [CHOOSE_ACCOUNT_TYPE_STEP]: [PERSONAL_DATA_STEP, ORGANIZATION_DATA_STEP],
  [PERSONAL_DATA_STEP]: [PERSONAL_CREDENTIALS_DATA_STEP],
  [ORGANIZATION_DATA_STEP]: [ORGANIZATION_CREDENTIALS_DATA_STEP],
  [PERSONAL_CREDENTIALS_DATA_STEP]: [SENT_CONFIRMATION_LETTER_STEP],
  [ORGANIZATION_CREDENTIALS_DATA_STEP]: [SENT_CONFIRMATION_LETTER_STEP],
  [SENT_CONFIRMATION_LETTER_STEP]: null,
};

const previousStepMap = {
  [PERSONAL_DATA_STEP]: CHOOSE_ACCOUNT_TYPE_STEP,
  [ORGANIZATION_DATA_STEP]: CHOOSE_ACCOUNT_TYPE_STEP,
  [PERSONAL_CREDENTIALS_DATA_STEP]: PERSONAL_DATA_STEP,
  [ORGANIZATION_CREDENTIALS_DATA_STEP]: ORGANIZATION_DATA_STEP,
  [SENT_CONFIRMATION_LETTER_STEP]: null,
  [SETUP_PASSWORD_STEP]: null,
};

const state = {
  currentStep: new LocalStore('register_').get('current_step', CHOOSE_ACCOUNT_TYPE_STEP),
  account: {
    type: new LocalStore('register_').get('account_type', null),
    name: new LocalStore('register_').get('account_name', null),
    lastName: new LocalStore('register_').get('account_last_name', null),
    patronymic: new LocalStore('register_').get('account_patronymic', null),
    activity: new LocalStore('register_').get('account_activity', null),
    organizationName: new LocalStore('register_').get('account_organization_name', null),
    contactPerson: new LocalStore('register_').get('account_contact_person', null),
    edrpou: new LocalStore('register_').get('account_edrpou', null),
    telephone: new LocalStore('register_').get('account_telephone', null),
    email: new LocalStore('register_').get('account_email', null),
    cfToken: null,
  },
  verifyEmailRequestTime: null,
  isLoadingRegister: false,
  registerError: null,
  isLoadingResendVerification: false,
  resendVerificationError: null,
  isLoadingEmailVerification: false,
  emailVerificationStatus: null,
  emailVerificationError: null,
  isLoadingSetupPassword: false,
  setupPasswordError: null,
  // Одноразовий токен з відповіді на верифікацію: лише ним встановлюється пароль
  setupPasswordToken: null,
};

const mutations = {
  SET_CURRENT_STEP: (state, step) => {
    state.currentStep = step;
    new LocalStore('register_').set('current_step', step);
  },
  SET_ACCOUNT_TYPE: (state, accountType) => {
    state.account.type = accountType;
    new LocalStore('register_').set('account_type', accountType);
  },
  SET_ACCOUNT_NAME: (state, accountName) => {
    state.account.name = accountName;
    new LocalStore('register_').set('account_name', accountName);
  },
  SET_ACCOUNT_LAST_NAME: (state, accountLastName) => {
    state.account.lastName = accountLastName;
    new LocalStore('register_').set('account_last_name', accountLastName);
  },
  SET_ACCOUNT_PATRONYMIC: (state, accountPatronymic) => {
    state.account.patronymic = accountPatronymic;
    new LocalStore('register_').set('account_patronymic', accountPatronymic);
  },
  SET_ACCOUNT_ACTIVITY: (state, accountActivity) => {
    state.account.activity = accountActivity;
    new LocalStore('register_').set('account_activity', accountActivity);
  },
  SET_ACCOUNT_ORGANIZATION_NAME: (state, accountOrganizationName) => {
    state.account.organizationName = accountOrganizationName;
    new LocalStore('register_').set('account_organization_name', accountOrganizationName);
  },
  SET_ACCOUNT_CONTACT_PERSON: (state, contactPerson) => {
    state.account.contactPerson = contactPerson;
    new LocalStore('register_').set('account_contact_person', contactPerson);
  },
  SET_ACCOUNT_EDRPOU: (state, edrpou) => {
    state.account.edrpou = edrpou;
    new LocalStore('register_').set('account_edrpou', edrpou);
  },
  SET_ACCOUNT_CF_TOKEN: (state, cfToken) => {
    state.account.cfToken = cfToken;
  },
  SET_ACCOUNT_TELEPHONE: (state, telephone) => {
    state.account.telephone = telephone;
    new LocalStore('register_').set('account_telephone', telephone);
  },
  SET_ACCOUNT_EMAIL: (state, email) => {
    state.account.email = email;
    new LocalStore('register_').set('account_email', email);
  },
  SET_LOADING_REGISTER: (state, isLoading) => {
    state.isLoadingRegister = isLoading;
  },
  SET_REGISTER_ERROR: (state, error) => {
    state.registerError = error;
  },
  SET_VERIFY_LETTER_TIME: (state) => {
    state.verifyEmailRequestTime = Date.now();
  },
  SET_LOADING_RESEND_VERIFY_LETTER: (state, isLoading) => {
    state.isLoadingResendVerification = isLoading;
  },
  SET_RESEND_VERIFY_LETTER_ERROR: (state, error) => {
    state.resendVerificationError = error;
  },
  SET_EMAIL_VERIFICATION_STATUS: (state, status) => {
    state.emailVerificationStatus = status;
  },
  SET_LOADING_EMAIL_VERIFICATION: (state, isLoading) => {
    state.isLoadingEmailVerification = isLoading;
  },
  SET_EMAIL_VERIFICATION_ERROR: (state, error) => {
    state.emailVerificationError = error;
  },
  SET_LOADING_SETUP_PASSWORD: (state, isLoading) => {
    state.isLoadingSetupPassword = isLoading;
  },
  SET_SETUP_PASSWORD_ERROR: (state, error) => {
    state.setupPasswordError = error;
  },
  SET_SETUP_PASSWORD_TOKEN: (state, token) => {
    state.setupPasswordToken = token;
  },
};

const actions = {
  switchCurrentStep({ commit, state }) {
    const validators = {
      [CHOOSE_ACCOUNT_TYPE_STEP]: () => !!state.account.type,
      [PERSONAL_DATA_STEP]: () => !!state.account.name && !!state.account.lastName && !!state.account.patronymic && !!state.account.activity,
      [ORGANIZATION_DATA_STEP]: () => !!state.account.organizationName && !!state.account.contactPerson && !!state.account.edrpou && !!state.account.activity,
      [PERSONAL_CREDENTIALS_DATA_STEP]: () => !!state.account.telephone && !!state.account.email,
      [ORGANIZATION_CREDENTIALS_DATA_STEP]: () => !!state.account.email,
      [SENT_CONFIRMATION_LETTER_STEP]: () => !!state.account.email,
      [SETUP_PASSWORD_STEP]: () => true,
    };

    const allowedSteps = allowedStepSwitches[state.currentStep];

    if (!allowedSteps?.length || !validators[state.currentStep]?.()) {
      return;
    }

    const nextStep = state.currentStep === CHOOSE_ACCOUNT_TYPE_STEP
      ? (state.account.type === 'personal' ? PERSONAL_DATA_STEP : ORGANIZATION_DATA_STEP)
      : allowedSteps[0];

    if (allowedSteps.includes(nextStep)) {
      commit('SET_CURRENT_STEP', nextStep);
    }
  },
  switchPreviousStep({ commit, state }) {
    const previousStep = state.currentStep === SENT_CONFIRMATION_LETTER_STEP
      ? (state.account.type === 'personal' ? PERSONAL_CREDENTIALS_DATA_STEP : ORGANIZATION_CREDENTIALS_DATA_STEP)
      : previousStepMap[state.currentStep];

    if (previousStep) {
      commit('SET_CURRENT_STEP', previousStep);
    }
  },
  setAccountType({ commit }, accountType) {
    commit('SET_ACCOUNT_TYPE', accountType);
  },
  setAccountPersonalData({ commit }, { name, lastName, patronymic, activity }) {
    commit('SET_ACCOUNT_NAME', name);
    commit('SET_ACCOUNT_LAST_NAME', lastName);
    commit('SET_ACCOUNT_PATRONYMIC', patronymic);
    commit('SET_ACCOUNT_ACTIVITY', activity);
  },
  setAccountOrganizationData({ commit }, { organizationName, contactPerson, edrpou, activity }) {
    commit('SET_ACCOUNT_ORGANIZATION_NAME', organizationName);
    commit('SET_ACCOUNT_CONTACT_PERSON', contactPerson);
    commit('SET_ACCOUNT_EDRPOU', edrpou);
    commit('SET_ACCOUNT_ACTIVITY', activity);
  },
  setAccountPersonalCredentials({ commit }, { telephone, email, cfToken = null }) {
    commit('SET_ACCOUNT_TELEPHONE', telephone);
    commit('SET_ACCOUNT_EMAIL', email);
    commit('SET_ACCOUNT_CF_TOKEN', cfToken);
  },
  setAccountOrganizationCredentials({ commit }, { email, cfToken = null }) {
    commit('SET_ACCOUNT_EMAIL', email);
    commit('SET_ACCOUNT_CF_TOKEN', cfToken);
  },
  async register({ commit, state, dispatch }){
    commit('SET_LOADING_REGISTER', true);
    commit('SET_REGISTER_ERROR', null);

    try {
      const response = await registerUser({
        type_id: state.account.type === 'personal' ? 0 : 1,
        email: state.account.email,
        name: state.account.type === 'personal' ? state.account.name : state.account.contactPerson,
        surname: state.account.lastName,
        patronymic: state.account.patronymic,
        activity_type_id: state.account.activity,
        company_name: state.account.organizationName,
        erdpy_code: state.account.edrpou,
        phone: state.account.telephone,
        cf_token: state.account.cfToken,
      });

      commit('SET_VERIFY_LETTER_TIME');
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Помилка при реєстрації нового користувача';

      commit('SET_REGISTER_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Помилка при реєстрації нового користувача',
          message: errorMessage,
        }, { root: true });
      }

      const responseCode = error?.response?.status || 0;
      if (responseCode === 422) {
        const errors = error?.response?.data?.data;
        const isOnlyEmailError =
          Object.keys(errors).length === 1 && errors.email && errors.email.length > 0;
        const message = errors
          ? Object.values(errors).flat().join('\n')
          : error?.response?.data?.message || errorMessage;

        if (isOnlyEmailError) {
          commit('SET_REGISTER_ERROR', null);
          return await dispatch('resendVerificationEmail');
        } else {
          commit('SET_REGISTER_ERROR', message);
        }
      }

      throw error;
    } finally {
      commit('SET_LOADING_REGISTER', false);
    }
  },
  async resendVerificationEmail({ commit, state, dispatch }) {
    const secondsElapsed = state.verifyEmailRequestTime
      ? Math.floor((Date.now() - state.verifyEmailRequestTime) / 1000)
      : VERIFY_LETTER_TIMEOUT;

    if (secondsElapsed < VERIFY_LETTER_TIMEOUT) {
      const errorMessage = `Відправити ще раз можна буде через ${VERIFY_LETTER_TIMEOUT - secondsElapsed} секунд`;
      throw new Error(errorMessage);
    }

    commit('SET_LOADING_RESEND_VERIFY_LETTER', true);
    commit('SET_RESEND_VERIFY_LETTER_ERROR', null);

    try {
      const response = await resendVerificationLetterAPI({ email: state.account.email });
      commit('SET_VERIFY_LETTER_TIME');

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Помилка відправки листа для підтвердження email адреси';

      commit('SET_RESEND_VERIFY_LETTER_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Помилка відправки листа для підтвердження email адреси',
          message: errorMessage,
        }, { root: true });
      }

      const responseCode = error?.response?.status || 0;
      if (responseCode === 422) {
        const errors = error?.response?.data?.data;
        const message = errors
          ? Object.values(errors).flat().join('\n')
          : error?.response?.data?.message || errorMessage;

        commit('SET_RESEND_VERIFY_LETTER_ERROR', message);
      }

      throw error;
    } finally {
      commit('SET_LOADING_RESEND_VERIFY_LETTER', false);
    }
  },
  getTimeUntilNextVerifyRequest({ state }) {
    if (!state.verifyEmailRequestTime) return 0;
    const secondsElapsed = Math.floor((Date.now() - state.verifyEmailRequestTime) / 1000);
    const timeLeft = VERIFY_LETTER_TIMEOUT - secondsElapsed;

    return timeLeft > 1 ? timeLeft : 0;
  },
  async verifyEmail({ commit, dispatch }, { email, token }) {
    commit('SET_LOADING_EMAIL_VERIFICATION', true);
    commit('SET_EMAIL_VERIFICATION_ERROR', null);
    commit('SET_EMAIL_VERIFICATION_STATUS', null);
    commit('SET_SETUP_PASSWORD_TOKEN', null);

    try {
      const response = await verifyEmailAPI({ email, token });
      commit('SET_EMAIL_VERIFICATION_STATUS', response?.status);

      if (response?.setupToken) {
        saveSetupToken(email, response.setupToken);
        commit('SET_SETUP_PASSWORD_TOKEN', response.setupToken);
      } else if (response?.status === 'verified') {
        // Сторінку оновили посеред встановлення пароля: токен ще міг не спливти
        commit('SET_SETUP_PASSWORD_TOKEN', readSavedSetupToken(email));
      }
      new LocalStore('register_').clear();

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не можливо підтвердити email адресу - email або токен не правильні';

      commit('SET_EMAIL_VERIFICATION_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не можливо підтвердити email адресу - email або токен не правильні',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_EMAIL_VERIFICATION', false);
    }
  },
  async setupPassword({ commit, dispatch }, { email, token, password }) {
    commit('SET_LOADING_SETUP_PASSWORD', true);
    commit('SET_SETUP_PASSWORD_ERROR', null);

    try {
      const authData = await setupPasswordAPI({ email, token, password });
      clearSavedSetupToken();

      return authData;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Помилка при створенні нового пароля';

      commit('SET_SETUP_PASSWORD_ERROR', errorMessage);

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Помилка при створенні нового пароля',
          message: errorMessage,
        }, { root: true });
      }

      const responseCode = error?.response?.status || 0;
      if (responseCode === 422) {
        const errors = error?.response?.data?.data;
        const message = errors
          ? Object.values(errors).flat().join('\n')
          : error?.response?.data?.message || errorMessage;

        commit('SET_SETUP_PASSWORD_ERROR', message);
      }

      throw error;
    } finally {
      commit('SET_LOADING_SETUP_PASSWORD', false);
    }
  },
};

const getters = {
  getSetupPasswordToken: (state) => state.setupPasswordToken,
  isRegisterLoading: state => state.isLoadingRegister,
  getRegisterError: state => state.registerError,
  isResendVerificationLoading: state => state.isLoadingResendVerification,
  getResendVerificationError: state => state.resendVerificationError,
  isEmailVerificationLoading: state => state.isLoadingEmailVerification,
  isEmailVerificationReady: state => !state.isLoadingEmailVerification && state.emailVerificationStatus !== null,
  getEmailVerificationError: state => state.emailVerificationError,
  getEmailVerificationStatus: state => state.emailVerificationStatus,
  isSetupPasswordLoading: state => state.isLoadingSetupPassword,
  getSetupPasswordError: state => state.setupPasswordError,

  getCurrentStep: state => state.currentStep,
  getAccountType: state => state.account.type,
  getAccountName: state => state.account.name,
  getAccountLastName: state => state.account.lastName,
  getAccountPatronymic: state => state.account.patronymic,
  getAccountActivity: state => state.account.activity,
  getAccountOrganizationName: state => state.account.organizationName,
  getAccountContactPerson: state => state.account.contactPerson,
  getAccountEDRPOU: state => state.account.edrpou,
  getAccountTelephone: state => state.account.telephone,
  getAccountEmail: state => state.account.email,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
